import { useParams, Outlet } from 'react-router-dom'
import {
  StandardLayout,
  PageTitle,
  Spinner,
  Center,
  Flex
} from '@sequencehq/core-components'
import { useInvoiceEditing } from 'Invoices/hooks/useInvoiceEditing'
import { useConnectBulkActions } from 'Invoices/hooks/useConnectBulkActions'
import { useCloseInvoiceEditor } from 'Invoices/hooks/useCloseInvoiceEditor'
import { Provider, useAtomValue, useSetAtom } from 'jotai'
import { match } from 'ts-pattern'
import {
  HeaderTitle,
  SplitLayout,
  InvoiceInspector,
  InvoiceWarningBanners,
  Preview
} from 'Invoices/components/CreateEditShared'
import { humanReadableInvoiceNumberAtom } from '../../atoms/invoiceEditing.atom'
import {
  editingRowIdAtom,
  editingRowLockedAtom
} from './sections/Summary/atoms'
import {
  Attachments,
  LineItems,
  Payments,
  Schedule,
  Summary,
  Usage
} from './sections'
import { HeaderActions } from './components'
import { ErrorMessage } from 'components/ErrorPages/ErrorMessage'
import { CONTAINER_STYLES } from 'Invoices/constants'
import { useStandardLayoutModalPortalRoot } from 'components/Layout/StandardLayoutModalPortalRoot'

type ViewState = 'error' | 'loading' | 'content'

const EditInvoicePageComponent = () => {
  const { id } = useParams()

  useConnectBulkActions()

  const invoiceEditing = useInvoiceEditing(id ?? '')

  const { isLoading, invoiceNotFound, invoiceIsError } = invoiceEditing

  const invoiceNumber = useAtomValue(humanReadableInvoiceNumberAtom)
  const closeInvoiceEditor = useCloseInvoiceEditor()
  const standardLayoutModalPortalRoot = useStandardLayoutModalPortalRoot()

  const hasError = invoiceIsError || invoiceNotFound
  const errorType = invoiceNotFound ? 'notFound' : 'general'

  const viewState: ViewState = match({ hasError, isLoading })
    .with({ hasError: true }, () => 'error' as const)
    .with({ isLoading: true }, () => 'loading' as const)
    .otherwise(() => 'content' as const)

  return (
    <>
      <PageTitle title={hasError ? [] : [invoiceNumber, 'Invoices']} />
      <StandardLayout
        asModal
        portalTarget={standardLayoutModalPortalRoot}
        header={
          <StandardLayout.Header
            padding="0 8px 0 16px" // align with inspector
            title={hasError ? null : <HeaderTitle showStatusBadge />}
            actions={
              hasError ? (
                <StandardLayout.ButtonClose
                  onClick={closeInvoiceEditor}
                  data-testid="header.button.close"
                />
              ) : (
                <HeaderActions />
              )
            }
          />
        }
        content={match(viewState)
          .with('error', () => <ErrorMessage errorType={errorType} />)
          .with('loading', () => (
            <Center width="100%" height="100%">
              <Spinner />
            </Center>
          ))
          .with('content', () => (
            <SplitLayout leadingSide={<Content />} trailingSide={<Preview />} />
          ))
          .exhaustive()}
        inspector={hasError ? undefined : <InvoiceInspector />}
      />
      {!hasError && <Outlet />}
    </>
  )
}

const Content = () => {
  const setEditingRowId = useSetAtom(editingRowIdAtom)
  const isEditingLocked = useAtomValue(editingRowLockedAtom)

  return (
    <Flex
      flex={1}
      // minHeight:0 lets this flex child shrink below its content so its own
      // overflowY scroll engages; without it the column parent stretches it to
      // content height and the bottom is clipped with nothing to scroll to.
      minHeight="0"
      overflowY="scroll"
      justifyContent="center"
      onClick={() => {
        if (!isEditingLocked) {
          setEditingRowId(null)
        }
      }}
    >
      <Flex
        flexDir="column"
        height="fit-content"
        width="100%"
        {...CONTAINER_STYLES}
      >
        <InvoiceWarningBanners />
        <Summary />
        <LineItems />
        <Payments />
        <Schedule />
        <Usage />
        <Attachments />
      </Flex>
    </Flex>
  )
}

export const EditInvoicePage = () => (
  <Provider>
    <EditInvoicePageComponent />
  </Provider>
)
